import IconSvg from "./IconSvg";
import "./style.css";
import {
  Briefcase,
  ShoppingCart,
  Landmark,
  BarChart2,
  Lightbulb,
  DollarSign,
  Search,
  List,
  Network,
  TrendingUp,
  Target,
  Smile,
  Megaphone,
  CheckSquare,
  Shield,
  Settings,
  Package,
  Users,
  Calculator,
  Laptop,
} from 'lucide-react';

const roleIcons = [
  {
    name: "B2B Sales",
    icon: Briefcase, // Represents business/work
  },
  {
    name: "B2C Sales",
    icon: ShoppingCart, // Directly relates to consumer sales
  },
  {
    name: "BFSI Sales",
    icon: Landmark, // Represents a bank or financial institution
  },
  {
    name: "Business Analytics",
    icon: BarChart2, // Ideal for data and analytics
  },
  {
    name: "Business Consulting",
    icon: Lightbulb, // Suggests ideas, solutions, and insight
  },
  {
    name: "Business Finance",
    icon: DollarSign, // Money and finance
  },
  {
    name: "Business Research",
    icon: Search, // Represents investigation and research
  },
  {
    name: "Category Management",
    icon: List, // For organization and categorization
  },
  {
    name: "Channel Sales",
    icon: Network, // Represents connections and distribution channels
  },
  {
    name: "Corporate Finance",
    icon: TrendingUp, // Growth and financial trends
  },
  {
    name: "Corporate Strategy",
    icon: Target, // Goals and strategic planning
  },
  {
    name: "Customer Success",
    icon: Smile, // Customer satisfaction and positive outcomes
  },
  {
    name: "Data Visualisation",
    icon: BarChart2, // (Same as Business Analytics, as visualization is key to it)
  },
  {
    name: "Digital Marketing",
    icon: Megaphone, // Broadcasting messages, promotion
  },
  {
    name: "Equity Research",
    icon: TrendingUp, // Analyzing market trends and growth
  },
  {
    name: "Financial Operations",
    icon: Calculator, // Calculation and financial processes
  },
  {
    name: "HR Generalist",
    icon: Users, // People management and human resources
  },
  {
    name: "Investment Banking",
    icon: Landmark, // (Same as BFSI Sales, representing financial institutions)
  },
  {
    name: "IT Business Analyst",
    icon: Laptop, // Technology and analysis
  },
  {
    name: "IT PreSales",
    icon: Megaphone, // Presenting and advocating solutions
  },
  {
    name: "IT Project Management",
    icon: CheckSquare, // Project tasks, completion, and organization
  },
  {
    name: "Market Research",
    icon: Search, // Investigating market trends
  },
  {
    name: "Marketing Management",
    icon: Megaphone, // Overarching marketing efforts
  },
  {
    name: "Performance Marketing",
    icon: TrendingUp, // Focusing on measurable growth and performance
  },
  {
    name: "Product Management",
    icon: Package, // Represents a product or new item
  },
  {
    name: "Risk Management",
    icon: Shield, // Protection and mitigation of risks
  },
  {
    name: "Service Operations",
    icon: Settings, // Gears/settings, representing operational processes
  },
  {
    name: "Supply Chain Management",
    icon: Network, // Connecting different parts of the supply chain
  },
  {
    name: "Talent Acquisition",
    icon: Users, // (Similar to HR Generalist, but more specific to hiring)
  },
  {
    name: "Tax and Accounting",
    icon: Calculator, // Financial calculations and bookkeeping
  },
  {
    name: "Accounting and Audit",
    icon: Calculator, // Financial calculations and bookkeeping
  },
  {
    name: "Tech Consulting",
    icon: Laptop, // Technology and advisory
  },
  {
    name: "Technology Sales",
    icon: Briefcase, // Sales within the tech sector
  },
];


const DemographicsCard = ({ data, onViewAllRoles }) => {
  const { genderDistribution, branchDistribution, topPreferredRoles, insight } =
    data;

  const getIcon = (role_name) => {
    const role = roleIcons.find(item => item.name === role_name)
    return role ? role.icon : null;
  }
  const filter_remove_percent_zero = genderDistribution.filter(
    (item) => item.percentage !== 0 && item.percentage !== ""
  );

  return (
    <div className="lg-col-span-4 card p-4">
      <div className="flex justify-between items-start mb-3">
        <span className="font-medium text-gray-800">Batch Demographics</span>
        <div className="tooltip-wrapper">
          <IconSvg
            path="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            className="h-3-5 w-3-5 text-gray-400"
          />
          <div className="tooltip-content w-48 ">
            Basic demographic information about the student batch including
            gender, branch, and role preferences.
          </div>
        </div>
      </div>

      {/* Gender Distribution */}
      <div className="flex items-center justify-center h-32 mb-2">
        <div className="w-full px-2">
          <h4 className="text-xs font-medium text-gray-700 mb-2 text-center">
            Gender Distribution
          </h4>
          <div className="relative w-full h-5 bg-gray-200 rounded-full mb-2">
            {filter_remove_percent_zero.map((item, index) => (
              <div
                key={item.label}
                className={`absolute top-0 h-5 bg-${item.color} ${index === 0 ? "rounded-l-full" : ""
                  } ${index === filter_remove_percent_zero.length - 1
                    ? "rounded-r-full"
                    : ""
                  }`}
                style={{
                  left: `${filter_remove_percent_zero
                    .slice(0, index)
                    .reduce((acc, curr) => acc + curr.percentage, 0)}%`,
                  width: `${item.percentage}%`,
                }}
              ></div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-600 px-1">
            {filter_remove_percent_zero.map((item) => (
              <span key={item.label} className="flex items-center">
                <IconSvg
                  path={item.iconPath}
                  className={`h-3 w-3 mr-1 text-${item.color.split("-")[0]
                    }-500`}
                />
                {item.label}: {item.percentage}%
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Branch Distribution */}
      {/* <div className="mb-3">
        <h4 className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
          Branch Distribution
          <div className="relative ml-1 group cursor-help">
            <IconSvg
              path="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              className="h-3-5 w-3-5 text-gray-400"
            />
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-black bg-opacity-80 rounded text-xs text-white opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity duration-200">
              Distribution of students across different academic branches in the
              current batch.
            </div>
          </div>
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {branchDistribution.map((branch) => (
            <div
              key={branch.name}
              className={`px-2 py-1 rounded-full text-xs font-medium ${branch.color} text-white`}
            >
              {branch.name} {branch.percentage}%
            </div>
          ))}
        </div>
      </div> */}

      {/* Top Preferred Roles */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
          Top Preferred Roles by Students
          <div className="tooltip-wrapper">
            <IconSvg
              path="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              className="h-3-5 w-3-5 text-gray-400"
            />
            <div className="tooltip-content w-48">
              Roles most frequently preferred by students based on their career
              preferences and aspirations.
            </div>
          </div>
        </h4>
        <div className="">
          {topPreferredRoles.map((role) => {
            const IconComponent = getIcon(role.name);

            return (
              <div key={role.name} className="preferred-role-item">
                <div className="preferred-role-icon-wrapper">
                  {IconComponent && (
                    <IconComponent
                      className="h-5 w-5"
                      strokeWidth={2.5}
                      size={16}
                      color="currentColor"
                    />
                  )}
                </div>
                <div className="flex-grow">
                  <div className="flex justify-between items-center">
                    <span className="preferred-role-name">{role.name}</span>
                    <span className="preferred-role-count">
                      {role.count} students
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="mt-2 border-t border-gray-100">
        <p className="text-xs text-gray-800">
          <span className="font-medium">Insight:</span> {insight}
        </p>
      </div>
      <div className="view-all-button-wrapper">
        <button onClick={onViewAllRoles} className="view-all-button">
          <IconSvg
            path="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            className="view-all-button-icon"
          />
          View all roles
        </button>
      </div>
    </div>
  );
};

export default DemographicsCard;
